import { useEffect } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { TextNode, $createTextNode } from 'lexical';
import { mergeRegister } from '@lexical/utils';
import { $createPlaceholderNode, $isPlaceholderNode } from '../nodes/PlaceholderNode';
import { notifications } from '../../../../lib/notifications';

const PLACEHOLDER_REGEX = /\{\{[^{}]+\}\}/g;
// Letters (any Unicode script), combining marks (Bengali vowels, diacritics, etc.),
// digits, hyphens, and underscores are allowed
const VALID_SLUG_RE = /^[\p{L}\p{M}\p{N}_-]+$/u;

// Debounce repeated warnings so the same invalid slug doesn't spam notifications
const recentlyWarned = new Set();

function transformTextNode(node) {
  const text = node.getTextContent();

  if ($isPlaceholderNode(node)) {
    // If the user edited the slug so it no longer matches {{...}}, demote to
    // a plain TextNode so the regex below can re-promote it when complete.
    if (/^\{\{[^{}]+\}\}$/.test(text)) return; // still valid — nothing to do
    const textNode = $createTextNode(text);
    textNode.setFormat(node.getFormat());
    textNode.setStyle(node.getStyle());
    node.replace(textNode);
    return;
  }
  if (!text.includes('{{')) return;

  const matches = [...text.matchAll(PLACEHOLDER_REGEX)];
  if (matches.length === 0) return;

  // Validate each slug before converting — reject any that contain forbidden symbols
  for (const match of matches) {
    const slug = match[0].slice(2, -2);
    if (!VALID_SLUG_RE.test(slug)) {
      if (!recentlyWarned.has(slug)) {
        recentlyWarned.add(slug);
        setTimeout(() => recentlyWarned.delete(slug), 3000);
        setTimeout(() => {
          notifications.show({
            title: 'Invalid placeholder name',
            message: `"${slug}" — placeholder names can only contain letters, digits, hyphens (-) and underscores (_). Symbols like / are not allowed.`,
            color: 'orange',
            autoClose: 5000,
          });
        }, 0);
      }
      return; // leave as plain text — don't convert
    }
  }

  // Build segments: alternating plain text and placeholder tokens
  const segments = [];
  let lastIndex = 0;

  for (const match of matches) {
    if (match.index > lastIndex) {
      segments.push({ text: text.slice(lastIndex, match.index), isPlaceholder: false });
    }
    segments.push({ text: match[0], isPlaceholder: true });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    segments.push({ text: text.slice(lastIndex), isPlaceholder: false });
  }

  if (segments.length <= 1 && !segments[0]?.isPlaceholder) return;

  // Replace the node with the split nodes
  let currentNode = node;
  segments.forEach((seg, i) => {
    let newNode;
    if (seg.isPlaceholder) {
      newNode = $createPlaceholderNode(seg.text);
      // Preserve inline style (font-size, color, etc.) from the source node so
      // that formatting applied before/after typing the {{token}} is not lost.
      const srcStyle = node.getStyle();
      if (srcStyle) newNode.setStyle(srcStyle);
    } else {
      newNode = $createTextNode(seg.text);
      newNode.setFormat(node.getFormat());
      newNode.setStyle(node.getStyle());
    }

    if (i === 0) {
      node.replace(newNode);
    } else {
      currentNode.insertAfter(newNode);
    }
    currentNode = newNode;
  });
}

export default function PlaceholderPlugin() {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return mergeRegister(
      editor.registerNodeTransform(TextNode, transformTextNode)
    );
  }, [editor]);

  return null;
}
